import type { TransactionDetails } from "../../../models/transaction";
import type { QuickEntryBillingFields, QuickEntrySendToAddress } from "./QuickEntryBillingPane";
import { RequestorFormConstants } from "./DataEntryFieldsConstants";

/** The two fields this seed owns. Tracked per field so each can seed when ITS value first arrives. */
/**
 * The fields this seed owns, as a value so callers can derive the count rather than hardcoding it
 * (review finding on PR !9605, MINOR-4: a hardcoded `size >= 2` would leave a third field
 * permanently unseedable once two had seeded).
 */
export const QUICK_ENTRY_BILLING_DEFAULT_FIELDS = ["feeScheduleId", "releaseMethodId"] as const;

export type QuickEntryBillingDefaultField = typeof QUICK_ENTRY_BILLING_DEFAULT_FIELDS[number];

export interface QuickEntryBillingDefaultsResult {
    readonly next: QuickEntryBillingFields;
    /** Which fields this pass actually seeded - the caller records these so they never re-seed. */
    readonly seeded: readonly QuickEntryBillingDefaultField[];
}

/**
 * Bug fix (AB#35088): computes the Quick Entry Billing pane's Fee Schedule / Release Method
 * defaults from the transaction, the way Classic Mode already does.
 *
 * Classic seeds both from transactionDetails (DataEntryDetailsRightPane:
 * `useState(props.transactionDetails.feeSchedule?.id ?? 0)`, and it passes
 * `transactionDetails.releaseMethod?.id` down to the billing group). Quick Entry only ever
 * initialised its caller-owned QuickEntryBillingFields to DEFAULT_QUICK_ENTRY_BILLING_FIELDS
 * (chargeForPostage alone) and never read the transaction, so both dropdowns rendered
 * `value=""` - i.e. "- Select -" - and Create Transaction stayed disabled until the
 * specialist re-picked values the transaction already carried.
 *
 * Extracted as a pure function (same seam as computeQuickEntryRehydration) so the precedence
 * rules are unit-testable without mounting DataEntryDetails.
 *
 * Precedence: a value already in `current` always wins - it is either the specialist's own
 * pick (pushed up by QuickEntryBillingPane's onFieldsChange) or an earlier seed. Uses nullish
 * checks, not falsy ones, so a legitimately-0 id is respected rather than re-seeded.
 *
 * PER-FIELD SEEDING (review finding on PR !9605). The two values do not necessarily arrive
 * together: `transactionDetails` is re-fetched during a session (getTransactionAttributes is
 * re-dispatched after several updates), so a payload can arrive with one of them populated and
 * the other absent, and the missing one can show up on a later refetch. A single
 * once-per-transaction "seeded" flag is therefore wrong in two ways - it burns even when nothing
 * was seeded, and it burns on a partial seed, so the later-arriving field never gets its default
 * and the Sev-1 symptom survives the fix. The caller passes the set of fields already seeded for
 * this transaction and this returns which ones it seeded now, so each field seeds exactly once,
 * when its own value first becomes available.
 *
 * `alreadySeeded` is what makes a specialist's deliberate CLEAR stick: once a field has seeded,
 * clearing it back to null must not re-trigger the default on the next refetch.
 *
 * Returns `null` when there is nothing to change, so the caller can skip a no-op setState.
 */
export function computeQuickEntryBillingDefaults(
    transactionDetails: TransactionDetails | undefined,
    current: QuickEntryBillingFields,
    alreadySeeded: ReadonlySet<QuickEntryBillingDefaultField> = new Set(),
): QuickEntryBillingDefaultsResult | null {
    if (!transactionDetails) {
        return null;
    }
    const feeScheduleId = transactionDetails.feeSchedule?.id;
    const releaseMethodId = transactionDetails.releaseMethod?.id;

    const seedFee = !alreadySeeded.has("feeScheduleId") && current.feeScheduleId == null && feeScheduleId != null;
    const seedRelease = !alreadySeeded.has("releaseMethodId") && current.releaseMethodId == null && releaseMethodId != null;
    if (!seedFee && !seedRelease) {
        return null;
    }

    const seeded: QuickEntryBillingDefaultField[] = [];
    if (seedFee) { seeded.push("feeScheduleId"); }
    if (seedRelease) { seeded.push("releaseMethodId"); }

    return {
        next: {
            ...current,
            ...(seedFee ? { feeScheduleId } : {}),
            ...(seedRelease ? { releaseMethodId } : {}),
        },
        seeded,
    };
}

/**
 * Bug fix (AB#35088, QA retest finding 2): adopts a release method the SERVER RECOMPUTED after a
 * requestor-contact change, which the once-per-field seeder above cannot do.
 *
 * The mechanism. Saving a send-to contact returns the server's recomputed `releaseMode`
 * (createSendToContactSaga / updateSendToContactSaga / patchSendToRequestorContactSaga), and the
 * `setReleaseMethod` reducer writes it onto `transactionDetails.releaseMethod` - which is what the
 * left/Summary pane reads (DataEntryDetailsLeftPane:1432), so Summary follows the contact
 * correctly. Quick Entry's Billing pane does not: `computeQuickEntryBillingDefaults` burns
 * `releaseMethodId` in `alreadySeeded` on the FIRST arrival and returns null forever after, so
 * removing the contact's fax left Billing showing Digital Fax while Summary showed Download - and
 * Create Transaction would then persist the fax method for a contact with no fax on file.
 *
 * Deliberately NOT folded into the seeder. The seeder's contract is "a value already present wins"
 * (that is what makes a specialist's deliberate pick and deliberate CLEAR stick); this is the
 * opposite rule for one field, and keying it off a CHANGE in the incoming value is what keeps the
 * two compatible.
 *
 * Classic parity. Classic runs exactly this: an effect keyed on the incoming
 * `props.releaseMethodId` that writes it into the form unconditionally
 * (DataEntryDetailsRightPaneBillingSection 40-44). It is safe there because Classic autosaves, so
 * an operator's own pick round-trips and the effect re-writes the same value. Keying on a change
 * of the INCOMING transaction value reproduces that in QE, where picks are not persisted until
 * Confirm All: an operator pick never moves `transactionDetails.releaseMethod`, so it is never
 * discarded by a re-render - only by a genuine server recomputation, which is Classic's behaviour
 * and the safe direction (a contact with no fax must not stay on Digital Fax).
 *
 * `lastSeenReleaseMethodId === undefined` means "not yet observed for this transaction": the first
 * arrival belongs to the seeder (and, on reopen, to rehydration), so this returns null for it and
 * the caller just records the value.
 *
 * Scope: `releaseMethodId` only. It is the one field the contact-change response recomputes;
 * `feeScheduleId` is not touched by that path and keeps the seeder's precedence unchanged.
 *
 * Returns null when there is nothing to change, so the caller can skip a no-op setState (and so
 * the componentDidUpdate loop terminates).
 */
export function computeQuickEntryReleaseMethodResync(
    transactionDetails: TransactionDetails | undefined,
    current: QuickEntryBillingFields,
    lastSeenReleaseMethodId: number | null | undefined,
): QuickEntryBillingFields | null {
    if (!transactionDetails) {
        return null;
    }
    const incoming = transactionDetails.releaseMethod?.id;
    // Nullish, not falsy - same rule as the seeder. A missing incoming value is not a signal to
    // clear what the operator already has.
    if (incoming == null) {
        return null;
    }
    if (lastSeenReleaseMethodId === undefined) {
        return null;   // first observation for this transaction - the seeder owns it
    }
    if (incoming === lastSeenReleaseMethodId) {
        return null;   // unchanged - the operator's own pick stands
    }
    if (current.releaseMethodId === incoming) {
        return null;   // already agrees
    }
    return { ...current, releaseMethodId: incoming };
}

/**
 * Bug fix (AB#37853, D1): merges billing defaults that arrive AFTER QuickEntryBillingPane has
 * mounted into the pane's own local state.
 *
 * Why this is needed. The pane keeps a local `useState(props.initialFields ?? {})` cache of the
 * caller-owned fields. A useState initializer runs only on first mount, so anything that lands
 * later is silently ignored. AB#35088's caller-side seeding (computeQuickEntryBillingDefaults,
 * above) was safe only because every seed landed BEFORE mount; AB#35289 then added a one-shot
 * post-Confirm-All re-fetch, while Confirm All flips to the Billing tab synchronously and the
 * pane is conditionally mounted. That inverted the ordering the earlier fix depended on. For an
 * Internal request the result is a deadlock rather than an inconvenience: Fee Schedule and
 * Release Method are locked, and creation is gated on exactly those two values.
 *
 * Classic already does the equivalent re-sync with an effect keyed on the incoming prop
 * (DataEntryDetailsRightPaneBillingSection 40-49). QE must NOT copy Classic's unconditional
 * overwrite, though: Classic autosaves every change, so the prop that comes back down is the
 * operator's own pick round-tripping. QE defers all persistence to Confirm All, so an
 * unconditional overwrite would destroy unsaved edits. Hence "fill only what is unset".
 *
 * Precedence:
 *   - adopt `incoming[k]` only when it is non-nullish AND `local[k]` is nullish;
 *   - nullish, not falsy - a legitimately-0 id counts as set (same rule as the seeder above);
 *   - a deliberate CLEAR stays cleared: clearing pushes the key up to the parent, whose
 *     `alreadySeeded` bookkeeping stops it being re-seeded, so it never comes back down here.
 *
 * Returns null when nothing would change. That is load-bearing, not a convenience: the parent
 * hands down state this child itself pushed up, so an unguarded setState in the syncing effect
 * is an infinite render loop. The null return is what terminates the parent/child echo.
 *
 * Pure - neither argument is mutated.
 */
export function mergeLateArrivingBillingFields(
    local: QuickEntryBillingFields,
    incoming: QuickEntryBillingFields | undefined,
): QuickEntryBillingFields | null {
    if (!incoming) {
        return null;
    }

    // Key-agnostic on purpose. The two ids above are what the Internal deadlock turns on, but the
    // caller seeds other billing fields too (chargeForPostage, and the Back-to-Quick-Entry round
    // trip restores the whole object), and a hardcoded key list would leave any future field
    // permanently unsyncable - the same trap MINOR-4 flagged on PR !9605.
    // Typed as Partial<QuickEntryBillingFields>, NOT Record<..., unknown>: an `unknown` value type
    // propagates through the spread below and makes every property of the result `unknown`, so the
    // return value stops matching the declared type (TS2322). The cast belongs at the write site
    // instead, which is where TS's real limitation is - it cannot correlate a union-typed key with
    // that key's own value type, and these members are `readonly`, so an indexed assignment fails on
    // both counts. Casting the RETURN expression instead would silence the error while leaving the
    // object untyped internally, and would hide a genuine mismatch if the field set ever diverges.
    const adopted: Partial<QuickEntryBillingFields> = {};
    let changed = false;
    for (const key of Object.keys(incoming) as (keyof QuickEntryBillingFields)[]) {
        if (incoming[key] != null && local[key] == null) {
            (adopted as Record<string, unknown>)[key] = incoming[key];
            changed = true;
        }
    }

    return changed ? { ...local, ...adopted } : null;
}

/**
 * AB#37981: the Bill To section's "Same as send to" copy source, and the source of the two
 * release-method delivery gates (Download needs an email, Digital Fax must match the fax).
 *
 * Reads the LIVE redux-form requestor fields (RequestorFormConstants.*) rather than
 * state.dataEntryState.requestorDetails, because that is what Classic reads for BOTH jobs:
 *
 *  - the copy: DataEntryDetailsRightPane's onCheck (~766-802) copies straight out of `values`;
 *  - the gates: localValidations.ts:327-328 takes `requestorFax`/`email` from
 *    values[RequestorFormConstants.FAX_NUMBER] / [EMAIL], then applies them at 368-387.
 *
 * This is a staleness fix, NOT a "the slice is always empty" fix - requestorDetails IS
 * populated in Quick Entry once the requestor is linked, because every QE path that links one
 * (createQuickEntryRequestor, saveRequestorContactNow, the classic requestor-modal's
 * updateSendToRequestor) re-dispatches getTransactionAttributes, whose saga loads it. What it
 * is not is CURRENT: onSelectRequestor and onEditRequestor write the requestor org/address to
 * the form only, so between an inline pick/edit and the next link+refetch the slice holds the
 * pre-edit values (or, before any link, the previous requestor's).
 *
 * Extracted as a pure function (same seam as computeQuickEntryBillingDefaults) so the mapping
 * is unit-testable without mounting DataEntryDetails.
 *
 * Returns undefined when there is nothing to copy - either no form values at all, or a requestor
 * form in which every one of the eleven mapped fields is still blank.
 *
 * The all-blank case is a review finding (round 6, thread 53045 finding 1) and it matters because
 * `values` always exists by the time Billing is reachable, so the old "only undefined for an
 * absent `values`" rule never fired in practice. An all-undefined OBJECT is truthy, so
 * QuickEntryBillingPane's onToggleSameAsSendTo took its copy branch and wrote `""` into all eleven
 * billTo* fields - and computeQuickEntryBillToDefaults' `current[field] != null` gate counts `""`
 * as filled, so a specialist who ticked "Same as send to" before Requestor Info was populated
 * permanently blocked the auto-populate seed for the rest of that session and got this ticket's
 * original blank Bill To back. Returning undefined leaves the pane's `update({ sameAsSendTo: true })`
 * branch to run, which touches no billTo* field, so the seed still lands when the record arrives.
 */
export function getQuickEntryBillingSendToAddressFromFormValues(
    values: Record<string, unknown> | undefined,
): QuickEntrySendToAddress | undefined {
    if (!values) {
        return undefined;
    }
    const asString = (v: unknown): string | undefined => (typeof v === "string" ? v : undefined);
    const mapped: QuickEntrySendToAddress = {
        requestorName: asString(values[RequestorFormConstants.REQUESTOR_NAME]),
        firstName: asString(values[RequestorFormConstants.FIRST_NAME]),
        lastName: asString(values[RequestorFormConstants.LAST_NAME]),
        addressLine1: asString(values[RequestorFormConstants.ADDRESS]),
        addressLine2: asString(values[RequestorFormConstants.ADDRESS2]),
        city: asString(values[RequestorFormConstants.CITY]),
        state: asString(values[RequestorFormConstants.STATE]),
        zip: asString(values[RequestorFormConstants.ZIP]),
        phone: asString(values[RequestorFormConstants.PHONE_NUMBER]),
        fax: asString(values[RequestorFormConstants.FAX_NUMBER]),
        email: asString(values[RequestorFormConstants.EMAIL]),
    };
    // Whitespace-trimmed, not just nullish: redux-form leaves a field the operator typed into and
    // then emptied as "", and a stray space is not a copyable requestor address either.
    if (Object.values(mapped).every(value => value == null || value.trim() === "")) {
        return undefined;
    }
    return mapped;
}

/** The eleven Bill To fields computeQuickEntryBillToDefaults owns, seeded per field like the two above. */
export const QUICK_ENTRY_BILL_TO_DEFAULT_FIELDS = [
    "billToRequestorName",
    "billToFirstName",
    "billToLastName",
    "billToAddressLine1",
    "billToAddressLine2",
    "billToCity",
    "billToState",
    "billToZip",
    "billToPhone",
    "billToFax",
    "billToEmail",
] as const;

export type QuickEntryBillToDefaultField = typeof QUICK_ENTRY_BILL_TO_DEFAULT_FIELDS[number];

/**
 * The `state.dataEntryState.requestorBillingDetails` slice (typed `any` there): the transaction's
 * own bill-to record, as returned by GET requestor/billtorequestor/{id}
 * (RequestorDataAccess.GetRequestorBilling -> ROIS_RequestorBilling_sel).
 */
export interface QuickEntryRequestorBillingDetails {
    /** Newtonsoft's CamelCasePropertyNamesContractResolver lowercases only the leading char of
     *  `RequestorBillingID`, so the wire name keeps the trailing acronym. Both spellings are read
     *  at the call site below so the transaction-identity check can't be defeated by a casing
     *  change on either side. */
    readonly requestorBillingID?: number;
    readonly requestorBillingId?: number;
    /** AB#37981 QA retest: which requestor this bill-to row belongs to. Read only to gate the
     *  contact fallback below - a Bill To deliberately pointed at a DIFFERENT organisation than the
     *  send-to requestor must not inherit the send-to's phone/fax/email. Already on the wire:
     *  RequestorDataAccess.GetRequestorBilling sets RequestorNameID from ROIS_RequestorBilling_sel.
     *  Both spellings for the same Newtonsoft casing reason as requestorBillingID above. */
    readonly requestorNameID?: number;
    readonly requestorNameId?: number;
    readonly requestorBillingLocation?: string;
    readonly firstName?: string;
    readonly lastName?: string;
    readonly address?: {
        readonly address1?: string;
        readonly address2?: string;
        readonly city?: string;
        readonly state?: string;
        readonly zipCode?: string;
    };
    readonly phone?: string;
    readonly fax?: string;
    readonly email?: string;
}

export interface QuickEntryBillToDefaultsResult {
    readonly next: QuickEntryBillingFields;
    readonly seeded: readonly QuickEntryBillToDefaultField[];
}

/**
 * AB#37981 QA retest (Renita Dorsey, 2026-08-21; names added for Tirumani Krishnasri's 2026-08-26
 * retest): the requestor contact details the Bill To seed may fall back to when the transaction's
 * own bill-to record does not carry them.
 *
 * These five and no more, and the boundary is not arbitrary - they are exactly the columns
 * tblRequestorBilling derives from the requestor CONTACT, so they are the ones blank on the rows
 * where the contact was linked rather than typed. They are also exactly the five Classic mirrors
 * into Bill To (RequestorContactPanel's onChange*(value, true) -> DataEntryDetailsRightPane's
 * `copyToBilling && changeFieldValue(...)`). Organisation name and address come from
 * tblRequestorName instead, seed reliably from the record, and are deliberately excluded: Classic
 * does not mirror them either, and widening the fallback would risk showing a bill-to address that
 * differs from the record a later save persists.
 *
 * Sourced by the caller from the LIVE redux-form requestor fields - the same source Classic's
 * "Same as send to" handler reads (DataEntryDetailsRightPane onCheck) - not from
 * state.dataEntryState.requestorDetails, which goes stale between an inline requestor pick/edit
 * and the next link+refetch.
 */
export interface QuickEntryBillToContactFallback {
    readonly firstName?: string;
    readonly lastName?: string;
    readonly phone?: string;
    readonly fax?: string;
    readonly email?: string;
    /** The send-to requestor these details belong to. The fallback is only applied when this
     *  matches the bill-to record's own requestor - see the gate in computeQuickEntryBillToDefaults. */
    readonly requestorNameId?: number;
}

/**
 * The bill-to record's own value where it has one, otherwise the live-form fallback.
 *
 * Whitespace-trimmed rather than merely nullish: ROIS_RequestorBilling_sel reads every text column
 * through .ToString(), so a NULL column arrives as "" and a record that never carried a phone is
 * indistinguishable from one whose phone was cleared. Both should fall back.
 */
function preferRecordValue(recordValue?: string, fallback?: string): string | undefined {
    if (typeof recordValue === "string" && recordValue.trim() !== "") {
        return recordValue;
    }
    return fallback;
}

/**
 * AB#37981: the SAME-REQUESTOR gate on using the live requestor contact at all.
 *
 * The fallback carries the SEND-TO requestor's contact, which is not by definition the bill-to
 * entity: Classic lets a specialist point Bill To at a different organisation entirely, and
 * applying the send-to's details there would put one organisation's contact on another's invoice.
 * So it applies only when the bill-to record's own RequestorNameID matches the send-to requestor.
 * A missing id on either side is not a match - absence is not a match, and record-only is the safe
 * default.
 *
 * Named rather than inline because the reasoning is worth finding from a call site, and because the
 * widened five-field fallback makes it load-bearing on more fields than when it was written.
 *
 * NOT checked here: that the record is this transaction's bill-to row. BOTH callers front-load it and
 * return null before reaching this - computeQuickEntryBillToDefaults and, since AB#37981 round 2,
 * computeQuickEntryBillToRecordResync. Duplicating it here would therefore be unreachable, and
 * unreachable guards cannot be tested. Any further caller must front-load it too; each caller's gate
 * is pinned by its own test (see the two "not this transaction's row" tests).
 */
export function billToContactFallbackForRecord(
    requestorBillingDetails: QuickEntryRequestorBillingDetails | undefined,
    liveRequestorContact: QuickEntryBillToContactFallback | undefined,
): QuickEntryBillToContactFallback | undefined {
    const billToRequestorId = requestorBillingDetails?.requestorNameID ?? requestorBillingDetails?.requestorNameId;
    const sameRequestor = billToRequestorId != null
        && liveRequestorContact?.requestorNameId != null
        && billToRequestorId === liveRequestorContact.requestorNameId;
    return sameRequestor ? liveRequestorContact : undefined;
}


/**
 * Bug fix (AB#37981): auto-populates the Quick Entry Billing pane's Bill To section from the
 * transaction's own bill-to record, the way Classic Mode already does.
 *
 * WHAT CLASSIC DOES, and why the reported symptom is a genuine QE-only gap. Both requestor-link
 * paths create the transaction's bill-to row server-side, seeded from the requestor:
 * `requestor/createSendTo` runs ROIS_SendToRequestor_ins and then ROIS_BillToRequestor_ins
 * (RequestorDataAccess.CreateSendToRequestor), and `transactions/sendToRequestor` runs
 * ROIS_TansactionRequestorNameID_upd, which INSERTs tblRequestorBilling from tblRequestorName +
 * tblRequestorContact and stamps tblRequestBuffer.RequestorBillingID. Both front ends then
 * re-dispatch getTransactionAttributes, whose saga loads that row into
 * state.dataEntryState.requestorBillingDetails once transactionDetails.billToContact.id is set
 * (DataEntryDetailsDucks.ts:3896-3899). Classic's mapStateToProps seeds every BILL_TO_* field
 * from that slice with SAME_AS_SEND_TO left false (DataEntryDetailsRightPane:2845-2856) - so the
 * specialist reaches Billing and finds Bill To already filled in, with no checkbox involved.
 * That is the legacy behaviour the bug's Image 4 shows. Quick Entry made the same POSTs and the
 * same refetch but never read the slice, so its Bill To rendered blank (Image 2).
 *
 * TRANSACTION IDENTITY IS CHECKED, not assumed. `requestorBillingDetails` is a single global
 * slice and is never cleared on a transaction switch, and the saga dispatches
 * getTransactionDetailsSuccess BEFORE getRequestorBillingDetailsSuccess - so there is a window
 * in which the new transaction's details are in props while the slice still holds the PREVIOUS
 * transaction's bill-to. Seeding then would write one patient's requestor address into another
 * transaction's billing and persist it at Create Transaction. The record therefore only seeds
 * when its own id matches the current transaction's `billToContact.id`; when the id is absent
 * this returns null (fail closed). Same hazard class as `currentTransactionDetails()`
 * (AB#31605 review MAJOR-1).
 *
 * Precedence mirrors computeQuickEntryBillingDefaults exactly: a value already in `current`
 * always wins (the specialist's own typing, pushed up by the pane's onFieldsChange, or an
 * earlier seed); `alreadySeeded` makes a deliberate CLEAR stick across the next refetch; each
 * field seeds once, when ITS value first arrives. Blank is treated as "not arrived" rather than
 * seeded, because ROIS_RequestorBilling_sel reads every text column through `.ToString()` and
 * so returns "" for a NULL - marking those seeded would burn the one chance a field has to pick
 * up a value a later save puts there.
 *
 * `sameAsSendTo` is deliberately NOT set: Classic leaves its checkbox unticked while showing the
 * seeded values, and ticking it here would arm the pane's toggle-off clear over data the
 * specialist never copied.
 *
 * Returns null when there is nothing to change, so the caller can skip a no-op setState.
 */
export function computeQuickEntryBillToDefaults(
    requestorBillingDetails: QuickEntryRequestorBillingDetails | undefined,
    billToContactId: number | undefined,
    current: QuickEntryBillingFields,
    alreadySeeded: ReadonlySet<QuickEntryBillToDefaultField> = new Set(),
    liveRequestorContact?: QuickEntryBillToContactFallback,
): QuickEntryBillToDefaultsResult | null {
    if (!requestorBillingDetails || billToContactId == null) {
        return null;
    }
    const recordId = requestorBillingDetails.requestorBillingID ?? requestorBillingDetails.requestorBillingId;
    if (recordId == null || recordId !== billToContactId) {
        return null;
    }

    // AB#37981 QA retest - the SAME-REQUESTOR GATE, and the reason this fallback is safe.
    //
    // The fallback carries the SEND-TO requestor's contact, which is not by definition the bill-to
    // entity: Classic lets a specialist point Bill To at a different organisation entirely. Seeding
    // that row with the send-to's phone/fax/email would put one organisation's contact details on
    // another's invoice. So the fallback applies ONLY when the bill-to record's own RequestorNameID
    // matches the send-to requestor. When either id is missing we take nothing - absence is not a
    // match, and the pre-fix behaviour (record-only) is the safe default.
    const contactFallback = billToContactFallbackForRecord(requestorBillingDetails, liveRequestorContact);

    const incoming = billToIncomingFromRecord(requestorBillingDetails, contactFallback);

    const adopted: Partial<QuickEntryBillingFields> = {};
    const seeded: QuickEntryBillToDefaultField[] = [];
    for (const field of QUICK_ENTRY_BILL_TO_DEFAULT_FIELDS) {
        const value = incoming[field];
        if (alreadySeeded.has(field) || current[field] != null || typeof value !== "string" || value.trim() === "") {
            continue;
        }
        // Cast for the same reason mergeLateArrivingBillingFields casts: TS cannot correlate a
        // union-typed key with that key's own value type, and these members are `readonly`.
        (adopted as Record<string, unknown>)[field] = value;
        seeded.push(field);
    }

    return seeded.length ? { next: { ...current, ...adopted }, seeded } : null;
}

/**
 * AB#37981 round 2: the eleven Bill To values the RECORD implies right now, with the live-contact
 * fallback already applied. Lifted verbatim out of computeQuickEntryBillToDefaults above so that the
 * seed and the re-sync below cannot disagree about what the record says - were they to diverge, the
 * re-sync would "correct" a field to a value the seed would never have produced.
 */
export function billToIncomingFromRecord(
    requestorBillingDetails: QuickEntryRequestorBillingDetails,
    contactFallback: QuickEntryBillToContactFallback | undefined,
): Readonly<Record<QuickEntryBillToDefaultField, string | undefined>> {
    return {
        billToRequestorName: requestorBillingDetails.requestorBillingLocation,
        // AB#37981 QA retest 2: the record's FirstName/LastName are contact-derived columns, so they
        // are blank on exactly the rows whose Phone/Fax/Email are blank. The previous cut left them
        // record-only on the belief that they "seed reliably from the record"; QA disproved that.
        billToFirstName: preferRecordValue(requestorBillingDetails.firstName, contactFallback?.firstName),
        billToLastName: preferRecordValue(requestorBillingDetails.lastName, contactFallback?.lastName),
        billToAddressLine1: requestorBillingDetails.address?.address1,
        billToAddressLine2: requestorBillingDetails.address?.address2,
        billToCity: requestorBillingDetails.address?.city,
        billToState: requestorBillingDetails.address?.state,
        billToZip: requestorBillingDetails.address?.zipCode,
        // AB#37981: the bill-to RECORD is authoritative where it has a value, but its five
        // contact-derived columns are sparsely populated while its address is not. Counted on aspDev
        // 2026-08-26, of 21,125 tblRequestorBilling rows: Address1 21,044, FirstName 8,524, LastName
        // 8,121, Phone 6,119, Fax 9,505, Email 5,209. So a specialist watches the address arrive and
        // the contact block stay empty - the split QA reported twice. (Sparse individually, not blank
        // on identical rows; the point is the contrast with Address1's 99.6%.) Falling back to the
        // live requestor contact is what makes "carries over automatically" true, and it is the same
        // source Classic's "Same as send to" copies from. Address and organisation name get NO
        // fallback - see the QuickEntryBillToContactFallback docblock for where that line is drawn.
        billToPhone: preferRecordValue(requestorBillingDetails.phone, contactFallback?.phone),
        billToFax: preferRecordValue(requestorBillingDetails.fax, contactFallback?.fax),
        billToEmail: preferRecordValue(requestorBillingDetails.email, contactFallback?.email),
    };
}

/** AB#37981 round 2: what the SEEDER actually wrote into each Bill To field, recorded at write time
 *  so ownership never has to be inferred. See computeQuickEntryBillToRecordResync. */
export type QuickEntryBillToWrittenValues = Partial<Record<QuickEntryBillToDefaultField, string>>;

/**
 * The bill-to ROW each written value came from - PER FIELD, not one value for the block.
 *
 * A single row id for the whole ledger was the first cut and it was defeated by the seeder, which is
 * row-blind: it keeps filling any field still unseeded from whatever row is in props now, so after a
 * requestor re-pick the ledger legitimately spans TWO rows (the fields seeded from the old row, plus
 * the previously-blank contact columns just seeded from the new one). A single id claimed the whole
 * ledger came from the new row, which switched the atomicity guard off on the very next pass and let
 * the mixed block through - reached by the same column sparsity that guard exists for.
 *
 * Per field, "the block spans more than one row" is detectable, which is the question that actually
 * matters.
 */
export type QuickEntryBillToWrittenRows = Partial<Record<QuickEntryBillToDefaultField, number | undefined>>;

/**
 * AB#37981 round 3 follow-up: the ledger fields whose DISPLAYED value already equals what the record
 * implies. Those fields describe the current row, whatever row they were originally written from, so
 * their recorded row must be advanced to it.
 *
 * Why this exists, because it is not obvious. The re-sync skips a field whose incoming value already
 * matches what was written - there is nothing to adopt. On a change of ROW that skip is a trap: the
 * field is fully reconciled with the new row, but if its recorded row is left behind then
 * `rowChanged` stays true forever, and from then on every pass is subjected to the all-or-none gate
 * and refused as soon as any one column is blank. The mechanism silently dies for the transaction.
 *
 * The trigger is mundane rather than exotic: two payers in the same State, or a same-requestor
 * re-save (which INSERTs a fresh bill-to row with identical content, so nothing is adopted at all and
 * the no-adoption path advances nothing). Found by review, reproduced by executing this module.
 *
 * Reads the record through the same billToIncomingFromRecord as the seed and the re-sync, so all
 * three agree on what the record says.
 */
export function billToFieldsReconciledWithRecord(
    requestorBillingDetails: QuickEntryRequestorBillingDetails | undefined,
    billToContactId: number | undefined,
    current: QuickEntryBillingFields,
    written: QuickEntryBillToWrittenValues,
    liveRequestorContact?: QuickEntryBillToContactFallback,
): readonly QuickEntryBillToDefaultField[] {
    if (!requestorBillingDetails || billToContactId == null) {
        return [];
    }
    const recordId = requestorBillingDetails.requestorBillingID ?? requestorBillingDetails.requestorBillingId;
    if (recordId == null || recordId !== billToContactId) {
        return [];
    }
    const contactFallback = billToContactFallbackForRecord(requestorBillingDetails, liveRequestorContact);
    const incoming = billToIncomingFromRecord(requestorBillingDetails, contactFallback);
    return QUICK_ENTRY_BILL_TO_DEFAULT_FIELDS.filter(field => {
        if (written[field] === undefined) {
            return false;   // not ours
        }
        const value = incoming[field];
        return typeof value === "string" && value.trim() !== "" && current[field] === value;
    });
}

/**
 * AB#37981 round 2 - QA's escalation (Tirumani Krishnasri, 2026-08-26): Classic re-derives Bill To
 * when the requestor is edited, Quick Entry does not, so as shipped a specialist has to be trained
 * to tick "Same as send to" or the wrong billing details are persisted.
 *
 * The server is ALREADY right and Quick Entry ALREADY holds the corrected row in props. Measured on
 * aspDev 2026-08-26:
 *   - a contact edit runs dbo.ROIS_SendToRequestorContact_upd, which mirrors FirstName / LastName /
 *     Email / Phone / Fax onto this transaction's own tblRequestorBilling row;
 *   - a requestor re-pick runs dbo.ROIS_TansactionRequestorNameID_upd, which INSERTS a new row
 *     seeded from the new requestor and re-stamps RequestorBillingID on the transaction.
 * The once-per-field seed then refuses the refreshed row, and Create Transaction PATCHes the stale
 * on-screen values back over it via dbo.ROIS_BillToRequestor_upd, which has no COALESCE on the name,
 * address or contact columns. So the correct row is actively overwritten - and because
 * dbo.spSReportSendInvoice reads the bill-to block as TEXT and dbo.StatementInfo_CreateNewStatements
 * groups monthly statements on RequestorBillingLocation TEXT, the charge lands on the previous
 * organisation's statement. Financial, not cosmetic.
 *
 * The rule: adopt the record's new value only into a field that still holds exactly what the seeder
 * put there. Two safety properties follow directly -
 *   - anything the specialist typed is theirs permanently (current !== written), and
 *   - a field the seeder never filled is never touched (written === undefined) - that stays the
 *     seeder's job, which is still watching for it.
 * A blank incoming value is never adopted, so a cleared column cannot wipe the screen.
 *
 * Ownership is RECORDED, not inferred. That is the whole difference from the mechanism withdrawn in
 * review on PR !10209, which deduced it by comparing against a snapshot of the live contact: when a
 * field went blank in that snapshot the memory of what we had written was erased, and the next record
 * change would overwrite a value the specialist had typed. A recorded write cannot go blank.
 *
 * Reads the record through the same billToIncomingFromRecord as the seed, so the transaction-identity
 * gate, the same-requestor gate and the live-contact fallback are all shared rather than restated.
 */
export function computeQuickEntryBillToRecordResync(
    requestorBillingDetails: QuickEntryRequestorBillingDetails | undefined,
    billToContactId: number | undefined,
    current: QuickEntryBillingFields,
    written: QuickEntryBillToWrittenValues,
    liveRequestorContact?: QuickEntryBillToContactFallback,
    writtenRows: QuickEntryBillToWrittenRows = {},
): { next: QuickEntryBillingFields, adopted: readonly QuickEntryBillToDefaultField[] } | null {
    if (!requestorBillingDetails || billToContactId == null) {
        return null;
    }
    // The record in the slice must be THIS transaction's row. requestorBillingDetails is a global
    // slice that is not cleared on an in-place transaction switch, so without this the re-sync could
    // act on the previous transaction's row - exactly what the seeder refuses to do.
    const recordId = requestorBillingDetails.requestorBillingID ?? requestorBillingDetails.requestorBillingId;
    if (recordId == null || recordId !== billToContactId) {
        return null;
    }
    const contactFallback = billToContactFallbackForRecord(requestorBillingDetails, liveRequestorContact);
    const incoming = billToIncomingFromRecord(requestorBillingDetails, contactFallback);

    // THE BLOCK IS THE UNIT OF OWNERSHIP, not the field. If the specialist has taken over ANY field
    // the seeder wrote, the whole Bill To block is theirs and nothing is adopted.
    //
    // Per-field ownership was the first cut and it is unsafe, which is the point of this guard. A
    // specialist who retypes only the organisation name - the payer is a law firm, say - and leaves
    // the seeded address would, on the next record change, keep "Baker & Co" while all ten other
    // fields moved to the new requestor's. That persists a name grouped onto one organisation's
    // statement beside another's street address, phone and email. Partly right is worse than
    // uniformly stale, because uniformly stale is visible and this is not.
    const ledgerFields = QUICK_ENTRY_BILL_TO_DEFAULT_FIELDS.filter(field => written[field] !== undefined);
    if (ledgerFields.some(field => current[field] !== written[field])) {
        return null;
    }

    // A CHANGE OF ROW IS A CHANGE OF PAYER, and must move every field the seeder wrote or none.
    //
    // A requestor re-pick inserts a new tblRequestorBilling row, and the row's contact columns are
    // blank on most rows (FirstName on 8,524 of 21,125; Phone 6,119; Email 5,209 - aspDev
    // 2026-08-26). Adopting only the non-blank ones would leave the previous payer's phone, fax,
    // email and suite line beside the new payer's name and street - and Create Transaction PATCHes
    // all eleven columns, so that mixture is what gets persisted and invoiced.
    //
    // When the block cannot move whole, THIS does not move it at all - which is the pre-fix
    // behaviour, and the honest description stops there.
    //
    // Do NOT read that as "the display stays uniformly the previous payer's". It may not, and an
    // earlier version of this comment claimed it did. The seeder runs FIRST in componentDidUpdate and
    // is row-blind: a blank record column is never marked seeded, so it stays fillable, and on a
    // re-pick the seeder will fill the columns that were blank on the old row FROM THE NEW ONE. The
    // block can therefore already span two rows by the time this runs, and refusing to adopt leaves
    // it that way. That mixture is the seeder's, not this function's - base 62721b1 ends in the same
    // state and has no re-sync at all - but the claim had to be corrected rather than left standing.
    // See the PR's Out of Scope: making the seeder row-aware is a behaviour change on a pre-existing
    // path with its own trade-off, because ROIS_BillToRequestor_upd has no COALESCE and leaving those
    // columns blank would PATCH blanks over the new row's real contact details. That
    // is a deliberate partial fix - see the PR's Out of Scope for the remaining case, which needs
    // the re-sync to be able to CLEAR a field the new row genuinely has no value for.
    // Any ledger entry written from a DIFFERENT row than the record now in props means the block does
    // not currently describe one payer, so it must be reconciled whole rather than field by field.
    const rowChanged = ledgerFields.some(field => writtenRows[field] !== recordId);
    const usable = (field: QuickEntryBillToDefaultField) => {
        const value = incoming[field];
        return typeof value === "string" && value.trim() !== "";
    };
    if (rowChanged && !ledgerFields.every(usable)) {
        return null;
    }

    const adoptedFields: QuickEntryBillToDefaultField[] = [];
    const adopted: Partial<QuickEntryBillingFields> = {};
    for (const field of ledgerFields) {
        if (!usable(field)) {
            continue;   // blank is never a signal to clear what is displayed - same rule as the seed
        }
        const value = incoming[field] as string;
        if (value === written[field]) {
            continue;   // the record still says what we wrote
        }
        (adopted as Record<string, unknown>)[field] = value;
        adoptedFields.push(field);
    }
    return adoptedFields.length ? { next: { ...current, ...adopted }, adopted: adoptedFields } : null;
}
